import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Checkbox, Divider, Form, Modal, TimePicker } from "antd";
import type React from "react";
import { useEffect, useState } from "react";
import type { DayOfWeek } from "../types";

interface DayAdjustmentModalProps {
	open: boolean;
	onClose: () => void;
	currentDay?: DayOfWeek;
	days: DayOfWeek[];
	onSave: (updatedDays: DayOfWeek[]) => void;
	primaryColor?: string;
	primaryLightColor?: string;
}

const BUSINESS_OPEN_TIME = 7;
const BUSINESS_CLOSE_TIME = 21;

export const DayAdjustmentModal: React.FC<DayAdjustmentModalProps> = ({
	open,
	onClose,
	currentDay,
	days,
	onSave,
	primaryColor = "#99CA29",
	primaryLightColor = "#fdfff0",
}) => {
	const [form] = Form.useForm();
	const [selectedDays, setSelectedDays] = useState<number[]>([]);

	useEffect(() => {
		if (!open) {
			form.resetFields();
			setSelectedDays([]);
		}
	}, [open, form]);

	useEffect(() => {
		if (days && open && currentDay) {
			const dayData = days.find((d) => d.value === currentDay.value);
			form.setFieldsValue({
				isEnabled: dayData?.enabled || false,
				times: dayData?.times || [{}],
			});
		}
	}, [days, open, currentDay, form]);

	const handleSelectedDay = (dayValue: number) => {
		if (selectedDays.includes(dayValue)) {
			setSelectedDays(selectedDays.filter((sd) => sd !== dayValue));
		} else {
			setSelectedDays([...selectedDays, dayValue]);
		}
	};

	const handleDayAdjustment = (data: {
		isEnabled: boolean;
		times: Array<{ startTime?: unknown; endTime?: unknown }>;
	}) => {
		if (!currentDay) return;

		const updatedDays = days.map((td) => {
			if (td.value === currentDay.value) {
				return {
					...td,
					enabled: data.isEnabled,
					times: data.times,
				};
			}
			if (selectedDays.includes(td.value)) {
				return {
					...td,
					times: data.times,
				};
			}
			return td;
		});

		onSave(updatedDays);
		onClose();
	};

	const disabledHours = () => {
		return [...Array(24).keys()].filter(
			(hour) => hour < BUSINESS_OPEN_TIME || hour > BUSINESS_CLOSE_TIME,
		);
	};

	const disabledMinutes = (selectedHour: number) => {
		if (selectedHour === BUSINESS_CLOSE_TIME) {
			return [...Array(60).keys()].filter((min) => min !== 0);
		}
		return [];
	};

	return (
		<Modal
			open={open}
			onCancel={onClose}
			title={`${currentDay?.longLabel || ""}のスケジュール`}
			footer={null}
			destroyOnClose
			centered
			zIndex={1005}
		>
			<div style={{ padding: 8 }}>
				<Form
					form={form}
					layout="vertical"
					initialValues={{
						isEnabled: false,
						times: [{}],
					}}
					onFinish={handleDayAdjustment}
					requiredMark={false}
					size="large"
				>
					<Form.Item name="isEnabled" valuePropName="checked">
						<Checkbox>{`${currentDay?.longLabel || ""}に予定を受け付けます`}</Checkbox>
					</Form.Item>

					<Form.List name="times">
						{(fields, { add, remove }) => (
							<>
								{fields.map((field) => (
									<div
										key={field.name}
										style={{ display: "flex", alignItems: "flex-start" }}
									>
										<Form.Item
											name={[field.name, "startTime"]}
											style={{ width: "calc(50% - 1.25rem)", marginRight: 8 }}
											extra="※開始時間"
											rules={[{ required: true, message: "必須です" }]}
										>
											<TimePicker
												inputReadOnly
												format="HH:mm"
												popupClassName="hide-timepicker-footer"
												style={{ width: "100%" }}
												showNow={false}
												allowClear={false}
												minuteStep={15}
												hideDisabledOptions
												disabledTime={() => ({
													disabledHours,
													disabledMinutes,
												})}
												onSelect={(value) => {
													const currentTimes = form.getFieldValue(
														"times",
													) as Array<{
														startTime?: unknown;
														endTime?: unknown;
													}>;
													form.setFieldValue(
														"times",
														currentTimes.map((t, i: number) =>
															i === field.name ? { ...t, startTime: value } : t,
														),
													);
												}}
											/>
										</Form.Item>
										<Form.Item
											name={[field.name, "endTime"]}
											style={{ width: "calc(50% - 1.25rem)", marginRight: 8 }}
											extra="※終了時間"
											rules={[{ required: true, message: "必須です" }]}
										>
											<TimePicker
												inputReadOnly
												format="HH:mm"
												popupClassName="hide-timepicker-footer"
												style={{ width: "100%" }}
												showNow={false}
												allowClear={false}
												minuteStep={15}
												hideDisabledOptions
												disabledTime={() => ({
													disabledHours,
													disabledMinutes,
												})}
												onSelect={(value) => {
													const currentTimes = form.getFieldValue(
														"times",
													) as Array<{
														startTime?: unknown;
														endTime?: unknown;
													}>;
													form.setFieldValue(
														"times",
														currentTimes.map((t, i: number) =>
															i === field.name ? { ...t, endTime: value } : t,
														),
													);
												}}
											/>
										</Form.Item>
										{fields.length > 1 && (
											<div
												style={{
													display: "flex",
													height: 40,
													alignItems: "center",
													justifyContent: "center",
												}}
											>
												<CloseOutlined
													style={{ fontSize: 20, cursor: "pointer" }}
													onClick={() => remove(field.name)}
												/>
											</div>
										)}
									</div>
								))}
								<Form.Item>
									<Button
										icon={<PlusOutlined />}
										type="text"
										style={{ padding: 0 }}
										onClick={() => add()}
									>
										時間帯を追加
									</Button>
								</Form.Item>
							</>
						)}
					</Form.List>

					<p style={{ fontWeight: "bold", marginBottom: 16 }}>
						他の曜日にも適用する（上書きされます）
					</p>
					<div style={{ display: "flex", marginBottom: 16 }}>
						{days
							.filter((day) => day.value !== currentDay?.value)
							.map((day) => (
								<button
									key={day.value}
									type="button"
									style={{
										border: selectedDays.includes(day.value)
											? `1px solid ${primaryColor}`
											: "1px solid #d9d9d9",
										borderRadius: 4,
										padding: "4px 16px",
										marginRight: 4,
										cursor: "pointer",
										backgroundColor: selectedDays.includes(day.value)
											? primaryLightColor
											: "white",
										background: selectedDays.includes(day.value)
											? primaryLightColor
											: "white",
									}}
									onClick={() => handleSelectedDay(day.value)}
								>
									<p style={{ margin: 0 }}>{day.label}</p>
								</button>
							))}
					</div>

					<Divider />

					<div style={{ display: "flex", justifyContent: "center", gap: 8 }}>
						<Button onClick={onClose}>キャンセル</Button>
						<Button
							type="primary"
							htmlType="submit"
							style={{
								backgroundColor: primaryColor,
								borderColor: primaryColor,
							}}
						>
							保存する
						</Button>
					</div>
				</Form>
			</div>
		</Modal>
	);
};
